import type { GetStaticPropsContext } from 'next'
import { NextSeo } from 'next-seo'
import Image from 'next/image'
import Link from 'next/link'

import NavTextNodes from '@data/i18n/nav.json'

import { getDocumentByUID } from '@lib/prismic/fetch/documents'
import sliceResolvers from '@lib/prismic/helpers/sliceResolvers'
import { formatDateString } from '@lib/utils/dates'

import AllSlices from '@components/prismic/AllSlices'

export default function Article({ post }: { post: any }) {
	// console.log('blog post:', post)

	return (
		<>
			<NextSeo title={post?.title?.[0]?.text} titleTemplate="%s | Birchbox Mag" />

			{/* Article */}
			<article className="pt-8 pb-24">
				{/* Header */}
				<header className="section py-16">
					<div className="container max-w-screen-lg">
						<p className="mb-6 font-medium text-gray-500">
							{/* Topic */}
							{post?.topics?.[0]?.topic?.uid && (
								<>
									<Link
										legacyBehavior
										href={`${NavTextNodes.theMagUrl}/${post.topics[0].topic.uid}`}
									>
										<a className="">{post?.topics?.[0]?.topic?.data?.title?.[0]?.text}</a>
									</Link>{' '}
									&middot;{' '}
								</>
							)}
							{/* Date */}
							{formatDateString(post?.date)}
						</p>

						{/* Title */}
						<h1 className="title text-5xl">{post?.title?.[0]?.text}</h1>
					</div>

					{/* Hero image */}
					{post?.hero_image?.large?.url && (
						<div className="bg-gray-100 h-[480px] relative mt-16 mr-[20%]">
							<Image
								src={post.hero_image.large.url}
								layout="fill"
								objectFit="cover"
								alt={post.hero_image.alt || post?.title?.[0]?.text || 'Magazine article image'}
							/>
						</div>
					)}
				</header>

				{/* Content */}
				<AllSlices body={post?.body} />

				{/* Author */}
				{post?.author?.data?.name?.[0]?.text && (
					<section className="md:ml-[10%] bg-gray-100 mt-24 max-md:pb-8">
						<div className="flex max-md:flex-col max-md:text-center items-center">
							{/* Image */}
							{post?.author?.data?.photo?.Square?.url && (
								<div className="aspect-square relative flex-shrink-0 w-64">
									<Image
										src={post.author.data.photo.Square.url}
										layout="fill"
										objectFit="cover"
										alt={post.author.data.photo.alt}
									/>
								</div>
							)}
							<div className="px-16 py-4">
								{/* Author */}
								<span className="inline-block font-medium text-sm text-gray-500 mb-2">Author</span>

								{/* Name */}
								{post?.author?.data?.name?.[0]?.text && (
									<h2 className="title text-3xl mb-4">{post.author.data.name[0].text}</h2>
								)}

								{/* Bio */}
								{post?.author?.data?.bio?.[0].text && (
									<p className="max-w-[480px]">{post.author.data.bio[0].text}</p>
								)}
							</div>
						</div>
					</section>
				)}
			</article>
		</>
	)
}

type GetStaticPropsParams = {
	uid: string
}

export async function getStaticProps({
	params,
	previewData
}: GetStaticPropsContext<GetStaticPropsParams>) {
	const { uid } = params ?? {}

	if (!uid)
		return {
			notFound: true
		}

	const post =
		(await getDocumentByUID(
			'article',
			uid,
			{
				fetchLinks: ['person.name', 'person.bio', 'person.name', 'person.photo', 'topic.title']
			},
			previewData
		)) ?? null

	if (post && post.type === 'article') {
		post.data.body = (await sliceResolvers(post.data.body)) as typeof post.data.body
	}

	return {
		props: {
			post: post?.data
		}
	}
}

export async function getStaticPaths() {
	// Pages are refreshed using on-demand ISR
	return {
		paths: [],
		fallback: 'blocking'
	}
}
